import { CommunityReply, useToggleLikeReply, useUpdateReply } from '@api/communityPostsApiHandler';
import PersonIcon from '@assets/images/icons/person.svg';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { MotionImage, MotionText, showToast } from '@shared/atoms';
import { SystemButton } from '@shared/molecules/SystemButton/SystemButton';
import { formatDateToDayShortMonthYear } from '@utils/date.util';
import { ToastMessages } from 'constants/toastMessages';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { LikeButton } from '../PostCard/PostCard';
import styles from './index.module.scss';

interface ReplyCardProps {
  reply: CommunityReply;
  onDeleteRequest: (replyId: number, onSuccess: () => void, onError: () => void) => void;
  refetchReplies: () => void;
}

export const ReplyCard: React.FC<ReplyCardProps> = ({
  reply: initialReply,
  onDeleteRequest,
  refetchReplies,
}) => {
  const [reply, setReply] = useState(initialReply);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);

  const updateReply = useUpdateReply();
  const toggleLike = useToggleLikeReply();

  // Update local reply state when prop changes
  useEffect(() => {
    setReply(initialReply);
  }, [initialReply]);

  const getNameAndDepartment = (postedBy: string) => {
    const parts = postedBy.split('•');

    return {
      name: parts[0]?.trim() || postedBy,
      department: parts[1]?.trim() || '',
    };
  };

  const { name, department } = getNameAndDepartment(reply.postedBy);

  const handleLike = useCallback(() => {
    const wasLiked = reply?.isLiked;

    // Show confetti animation when liking
    if (!wasLiked) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1000);
    }

    // Optimistic update
    setReply((prev) => ({
      ...prev,
      isLiked: !prev.isLiked,
      likesCount: prev.isLiked ? prev.likesCount - 1 : prev.likesCount + 1,
    }));

    toggleLike.mutate(reply.id, {
      onSuccess: (response) => {
        // Update with server response
        setReply((prev) => ({
          ...prev,
          isLiked: response.responseObject?.isLiked ?? prev.isLiked,
          likesCount: response.responseObject?.likesCount ?? prev.likesCount,
        }));
      },
      onError: () => {
        // Revert on error
        setReply(initialReply);
      },
    });
  }, [reply.id, toggleLike, initialReply]);

  const handleEdit = useCallback(() => {
    setEditContent(reply.content);
    setIsEditing(true);
  }, [reply.content]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditContent('');
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editContent.trim() || editContent.length > 5000) {
      return;
    }

    updateReply.mutate(
      { replyId: reply.id, content: editContent },
      {
        onSuccess: (response) => {
          setReply(response.responseObject || reply);
          setIsEditing(false);
          setEditContent('');
          showToast(
            ToastMessages.communityFeed.success.YOUR_REPLY_UPDATED_SUCCSSFULLY,
            undefined,
            'success',
          );
        },
        onError: () => {
          showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
        },
      },
    );
  }, [editContent, reply, updateReply]);

  const handleDelete = useCallback(() => {
    const onSuccess = async () => {
      showToast(
        ToastMessages.communityFeed.success.YOUR_REPLY_REMOVED_SUCCSSFULLY,
        undefined,
        'success',
      );

      await refetchReplies();
    };

    const onError = () => {
      showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
    };

    onDeleteRequest(reply.id, onSuccess, onError);
  }, [reply.id, onDeleteRequest]);

  return (
    <motion.div
      className={styles.replyCard}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.replyHeader}>
        <div className={styles.replyAvatar}>
          <MotionImage src={PersonIcon} alt="Chat Icon" className={styles.chatIcon} />
        </div>
        <div className={styles.replyHeaderInfo}>
          <div className={styles.replyAuthorLine}>
            <MotionText as="h4" text={name} className={styles.replyAuthorName} />
            {department && (
              <>
                <span className={styles.replyDot}>•</span>
                <MotionText as="text" text={department} className={styles.replyDepartment} />
              </>
            )}
          </div>
          <MotionText
            as="text"
            text={`Replied on ${formatDateToDayShortMonthYear(reply?.createdAt)}`}
            className={styles.replyDate}
          />
        </div>
        {reply.isOwner && !isEditing && (
          <div className={styles.replyActions}>
            <motion.button
              className={styles.editBtn}
              onClick={handleEdit}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <EditIcon fontSize="small" />
            </motion.button>
            <motion.button
              className={styles.deleteBtn}
              onClick={handleDelete}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </motion.button>
          </div>
        )}
      </div>

      {/* Content - Editable or Static */}
      {isEditing ? (
        <div className={styles.editReplyContainer}>
          <textarea
            className={styles.editTextarea}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            maxLength={5000}
            rows={3}
          />
          <div className={styles.editActions}>
            <MotionText
              as="text"
              text={`${editContent.length}/5000`}
              className={styles.charCount}
            />
            <div className={styles.editButtons}>
              <SystemButton
                variant="outlinedPrimarySkyBlue"
                onClick={handleCancelEdit}
                size="small"
                disabled={updateReply.isPending}
              >
                Cancel
              </SystemButton>
              <SystemButton
                variant="primaryDarkSkyBlue"
                onClick={handleSaveEdit}
                size="small"
                disabled={!editContent.trim() || updateReply.isPending}
              >
                {updateReply.isPending ? 'Saving...' : 'Save'}
              </SystemButton>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.replyContent}>
          <MotionText as="text" text={reply.content} />
        </div>
      )}

      {/* Like Button */}
      {!isEditing && (
        <div className={styles.replyFooter}>
          <LikeButton
            isLiked={reply.isLiked}
            likesCount={reply?.likesCount || 0}
            onLike={handleLike}
            disabled={toggleLike.isPending}
            showConfetti={showConfetti}
          />
        </div>
      )}
    </motion.div>
  );
};
